import { useRef, useState } from 'react';
import { Building2, Handshake, Phone, Plus, Trash2, Users as UsersIcon } from 'lucide-react';
import type { CrmCompanySummary, CrmContactSummary, CrmSector, Deal, DealStage, Workspace } from '@unitly/contracts';
import { ACTIVITY_KIND_LABELS, ACTIVITY_KINDS, CRM_GROUP_BY, CRM_METRICS, CRM_SECTOR_FIELDS, CRM_SECTOR_LABELS, CRM_SECTORS } from '@unitly/contracts';
import {
  useAddCrmActivity, useCreateDeal, useCrmActivities, useCrmCompanies, useCrmCompany, useCrmCompanyMutation, useCrmConfig,
  useCrmContact, useCrmContactMutation, useCrmContacts, useCrmSettings, useDeals, useDeleteCrmCompany, useDeleteCrmContact, useDeleteDeal,
  useMoveDeal, useUpdateDeal,
} from '../../api/queries.ts';
import { useOrgCtx } from '../../app/org-context.tsx';
import { Sheet } from '../../ui/Sheet.tsx';
import { EmptyState, PageLoader } from '../../ui/primitives.tsx';
import { useToast } from '../../ui/Toast.tsx';
import { ApiError } from '../../api/client.ts';
import { WidgetBoard } from '../widgets/ConfigurableChart.tsx';
import { EmailComposer, EmailHistory } from './CrmEmail.tsx';

const money = (cents: number, currency: string) => (cents / 100).toLocaleString('it-IT', { style: 'currency', currency });

export type CrmSection = 'pipeline' | 'companies' | 'contacts' | 'report';

export function CrmView({ workspace, section }: { workspace: Workspace; section: CrmSection }) {
  const { orgId } = useOrgCtx();
  const config = useCrmConfig(orgId, workspace.id);

  if (config.isLoading) return <PageLoader />;
  const cfg = config.data;
  if (!cfg?.enabled) return <EnableCrm workspace={workspace} canEnable={Boolean(cfg?.permissions.admin)} />;

  return (
    <>
      {section === 'pipeline' && <Pipeline workspace={workspace} stages={cfg.stages} currency={cfg.currency} canCreate={cfg.permissions.create} />}
      {section === 'companies' && <Companies workspace={workspace} canCreate={cfg.permissions.create} />}
      {section === 'contacts' && <Contacts workspace={workspace} canCreate={cfg.permissions.create} />}
      {section === 'report' && <WidgetBoard workspaceId={workspace.id} module="crm" metrics={[...CRM_METRICS]} groupBys={[...CRM_GROUP_BY]} canEdit={cfg.permissions.admin} />}
    </>
  );
}

function EnableCrm({ workspace, canEnable }: { workspace: Workspace; canEnable: boolean }) {
  const { orgId } = useOrgCtx();
  const enable = useCrmSettings(orgId, workspace.id);
  const toast = useToast();
  return (
    <div className="card card-pad" style={{ textAlign: 'center', display: 'flex', flexDirection: 'column', alignItems: 'center', gap: 10, padding: 28 }}>
      <span className="empty" style={{ padding: 0 }}><span className="icon" style={{ background: 'var(--accent-soft)', color: 'var(--accent)' }}><Handshake size={22} /></span></span>
      <strong style={{ fontSize: 18 }}>CRM & Vendite di {workspace.name}</strong>
      <p style={{ margin: 0, color: 'var(--label-2)', maxWidth: '46ch', fontSize: 15 }}>
        Aziende, contatti e una pipeline di trattative con fasi personalizzabili.
      </p>
      {canEnable ? (
        <button type="button" className="btn btn-primary" disabled={enable.isPending}
          onClick={() => enable.mutate({ path: '/enable', method: 'POST' }, { onSuccess: () => toast('CRM attivato'), onError: (e) => toast(e.message) })}>
          Attiva il CRM
        </button>
      ) : (
        <p className="hint">Chiedi a chi gestisce lo spazio di attivarlo.</p>
      )}
    </div>
  );
}

// ------------------------------------------------------------------ pipeline (deal kanban)
function Pipeline({ workspace, stages, currency, canCreate }: { workspace: Workspace; stages: DealStage[]; currency: string; canCreate: boolean }) {
  const { orgId } = useOrgCtx();
  const deals = useDeals(orgId, workspace.id);
  const move = useMoveDeal(orgId, workspace.id);
  const updateDeal = useUpdateDeal(orgId, workspace.id);
  const [over, setOver] = useState<string | null>(null);
  const [editing, setEditing] = useState<Deal | 'new' | null>(null);
  const newSeq = useRef(0);
  const openNew = () => { newSeq.current += 1; setEditing('new'); };

  return (
    <>
      <div className="toolbar">
        {canCreate && <button type="button" className="btn btn-primary btn-sm" onClick={openNew}><Plus size={16} /> Trattativa</button>}
      </div>
      {deals.isLoading && <PageLoader />}
      {deals.data && (
        <div className="board" role="list" aria-label="Pipeline vendite">
          {stages.map((s) => {
            const items = (deals.data ?? []).filter((d) => d.state.id === s.id);
            const total = items.reduce((sum, d) => sum + d.valueCents, 0);
            return (
              <div key={s.id} role="listitem" className={`board-col${over === s.id ? ' over' : ''}`}
                onDragOver={(e) => { e.preventDefault(); setOver(s.id); }}
                onDragLeave={() => setOver((o) => (o === s.id ? null : o))}
                onDrop={(e) => {
                  e.preventDefault();
                  setOver(null);
                  const id = e.dataTransfer.getData('text/deal-id');
                  const deal = items.concat(deals.data ?? []).find((d) => d.id === id);
                  if (!deal || deal.state.id === s.id || !deal.permissions.edit) return;
                  move.mutate({ id, stateId: s.id }, {
                    onSuccess: () => {
                      if (s.category !== 'canceled' || deal.lostReason) return;
                      const reason = window.prompt('Perché è stata persa questa trattativa? (facoltativo)');
                      if (reason && reason.trim()) updateDeal.mutate({ id, patch: { lostReason: reason.trim() } });
                    },
                  });
                }}>
                <div className="board-col-head">
                  <span className="pill" data-cat={s.category}>{s.name}</span>
                  <span className="n">{items.length}</span>
                </div>
                {total > 0 && <p className="hint" style={{ margin: '0 0 6px', fontSize: 12.5 }}>{money(total, currency)}</p>}
                {items.map((d) => (
                  <button key={d.id} type="button" className="board-card" draggable={d.permissions.edit}
                    onDragStart={(e) => e.dataTransfer.setData('text/deal-id', d.id)} onClick={() => setEditing(d)}>
                    <span className="task-title">{d.title}</span>
                    <div className="secondary" style={{ display: 'flex', gap: 8, flexWrap: 'wrap', fontSize: 12.5 }}>
                      {d.valueCents > 0 && <span>{money(d.valueCents, currency)}</span>}
                      {d.company && <span>{d.company.name}</span>}
                      {d.owner && <span>{d.owner.displayName}</span>}
                    </div>
                    {s.category === 'canceled' && d.lostReason && (
                      <div className="secondary" style={{ fontSize: 12, color: 'var(--red)' }}>{d.lostReason}</div>
                    )}
                  </button>
                ))}
              </div>
            );
          })}
        </div>
      )}
      <DealSheet
        key={editing === 'new' ? `new-${newSeq.current}` : (editing?.id ?? 'closed')}
        workspace={workspace} stages={stages} deal={editing === 'new' ? null : editing} isNew={editing === 'new'}
        open={editing !== null} onClose={() => setEditing(null)}
      />
    </>
  );
}

function DealSheet({ workspace, stages, deal, isNew, open, onClose }: { workspace: Workspace; stages: DealStage[]; deal: Deal | null; isNew: boolean; open: boolean; onClose: () => void }) {
  const { orgId } = useOrgCtx();
  const companies = useCrmCompanies(orgId, workspace.id);
  const contacts = useCrmContacts(orgId, workspace.id);
  const create = useCreateDeal(orgId, workspace.id);
  const update = useUpdateDeal(orgId, workspace.id);
  const del = useDeleteDeal(orgId, workspace.id);
  const activities = useCrmActivities(orgId, workspace.id, { dealId: deal?.id });
  const addActivity = useAddCrmActivity(orgId, workspace.id);
  const toast = useToast();
  const [form, setForm] = useState(() => ({
    title: deal?.title ?? '', companyId: deal?.company?.id ?? '', contactId: deal?.contact?.id ?? '',
    valueCents: deal ? deal.valueCents / 100 : 0, stateId: deal?.state.id ?? stages[0]?.id ?? '', probability: deal?.probability ?? 50,
    lostReason: deal?.lostReason ?? '',
  }));
  const [note, setNote] = useState('');
  const [key, setKey] = useState(deal?.id ?? 'new');
  if ((deal?.id ?? 'new') !== key) {
    setForm({
      title: deal?.title ?? '', companyId: deal?.company?.id ?? '', contactId: deal?.contact?.id ?? '', valueCents: deal ? deal.valueCents / 100 : 0,
      stateId: deal?.state.id ?? stages[0]?.id ?? '', probability: deal?.probability ?? 50, lostReason: deal?.lostReason ?? '',
    });
    setKey(deal?.id ?? 'new');
    setNote('');
  }
  const stageCategory = stages.find((s) => s.id === form.stateId)?.category;

  const save = () => {
    const patch = {
      title: form.title, companyId: form.companyId || null, contactId: form.contactId || null,
      valueCents: Math.round(form.valueCents * 100), stateId: form.stateId, probability: form.probability,
      lostReason: stageCategory === 'canceled' ? (form.lostReason.trim() || null) : null,
    };
    const onError = (e: unknown) => toast(e instanceof ApiError ? e.message : 'Salvataggio non riuscito');
    if (isNew) create.mutate(patch, { onSuccess: onClose, onError });
    else update.mutate({ id: deal!.id, patch }, { onSuccess: onClose, onError });
  };

  return (
    <Sheet open={open} onClose={onClose} variant="center" title={isNew ? 'Nuova trattativa' : 'Trattativa'} labelledBy="deal-title"
      footer={
        <div style={{ display: 'flex', gap: 8, width: '100%' }}>
          {!isNew && deal?.permissions.delete && (
            <button type="button" className="btn btn-plain" onClick={() => { if (confirm('Eliminare questa trattativa?')) del.mutate(deal.id, { onSuccess: onClose }); }}>
              <Trash2 size={16} />
            </button>
          )}
          <span className="spacer" />
          <button type="button" className="btn btn-secondary" onClick={onClose}>Annulla</button>
          <button type="button" className="btn btn-primary" disabled={!form.title.trim() || create.isPending || update.isPending} onClick={save}>Salva</button>
        </div>
      }>
      <label className="field"><span>Titolo</span><input className="input" value={form.title} onChange={(e) => setForm({ ...form, title: e.target.value })} /></label>
      <div className="field">
        <span>Azienda</span>
        <select className="input" value={form.companyId} onChange={(e) => setForm({ ...form, companyId: e.target.value })}>
          <option value="">—</option>
          {companies.data?.map((c) => <option key={c.id} value={c.id}>{c.name}</option>)}
        </select>
      </div>
      <div className="field">
        <span>Contatto</span>
        <select className="input" value={form.contactId} onChange={(e) => setForm({ ...form, contactId: e.target.value })}>
          <option value="">—</option>
          {contacts.data?.map((c) => <option key={c.id} value={c.id}>{c.firstName} {c.lastName}</option>)}
        </select>
      </div>
      <div style={{ display: 'flex', gap: 10 }}>
        <label className="field" style={{ flex: 1 }}><span>Valore (€)</span>
          <input className="input" type="number" min={0} step={50} value={form.valueCents} onChange={(e) => setForm({ ...form, valueCents: Number(e.target.value) })} />
        </label>
        <label className="field" style={{ flex: 1 }}><span>Probabilità %</span>
          <input className="input" type="number" min={0} max={100} value={form.probability} onChange={(e) => setForm({ ...form, probability: Number(e.target.value) })} />
        </label>
      </div>
      <div className="field">
        <span>Fase</span>
        <select className="input" value={form.stateId} onChange={(e) => setForm({ ...form, stateId: e.target.value })}>
          {stages.map((s) => <option key={s.id} value={s.id}>{s.name}</option>)}
        </select>
      </div>
      {stageCategory === 'canceled' && (
        <label className="field"><span>Motivo della perdita</span>
          <textarea className="textarea" rows={2} value={form.lostReason} onChange={(e) => setForm({ ...form, lostReason: e.target.value })} placeholder="Facoltativo: perché non si è chiusa?" />
        </label>
      )}

      {!isNew && (
        <>
          <hr className="sep" />
          <h3 className="subhead">Attività</h3>
          <div className="list" style={{ marginBottom: 10 }}>
            {(activities.data ?? []).map((a) => (
              <div key={a.id} className="list-row" style={{ cursor: 'default' }}>
                <span className="grow">
                  <div><strong>{ACTIVITY_KIND_LABELS[a.kind]}</strong> · {a.createdBy.displayName}</div>
                  <div className="secondary">{a.body}</div>
                </span>
              </div>
            ))}
            {activities.data?.length === 0 && <p className="hint" style={{ padding: 12, margin: 0 }}>Nessuna attività registrata.</p>}
          </div>
          <div style={{ display: 'flex', gap: 6 }}>
            <select className="input" style={{ width: 110 }} defaultValue="call" id="activity-kind">
              {ACTIVITY_KINDS.map((k) => <option key={k} value={k}>{ACTIVITY_KIND_LABELS[k]}</option>)}
            </select>
            <input className="input" style={{ flex: 1 }} placeholder="Aggiungi una nota…" value={note} onChange={(e) => setNote(e.target.value)}
              onKeyDown={(e) => {
                if (e.key !== 'Enter' || !note.trim()) return;
                const kind = (document.getElementById('activity-kind') as HTMLSelectElement).value as typeof ACTIVITY_KINDS[number];
                addActivity.mutate({ kind, body: note, dealId: deal!.id }, { onSuccess: () => setNote('') });
              }} />
          </div>
          {deal && (
            <>
              <hr className="sep" />
              <h3 className="subhead">Email</h3>
              <EmailHistory workspace={workspace} dealId={deal.id} />
            </>
          )}
        </>
      )}
    </Sheet>
  );
}

// ------------------------------------------------------------------ companies
function Companies({ workspace, canCreate }: { workspace: Workspace; canCreate: boolean }) {
  const { orgId } = useOrgCtx();
  const companies = useCrmCompanies(orgId, workspace.id);
  const del = useDeleteCrmCompany(orgId, workspace.id);
  const [editing, setEditing] = useState<CrmCompanySummary | 'new' | null>(null);
  const newSeq = useRef(0);
  const openNew = () => { newSeq.current += 1; setEditing('new'); };

  return (
    <>
      <div className="toolbar">{canCreate && <button type="button" className="btn btn-primary btn-sm" onClick={openNew}><Plus size={16} /> Azienda</button>}</div>
      {companies.isLoading && <PageLoader />}
      {companies.data?.length === 0 && <div className="card"><EmptyState tone="neutral" title="Nessuna azienda">Aggiungi la prima azienda del vostro portafoglio clienti.</EmptyState></div>}
      {companies.data && companies.data.length > 0 && (
        <div className="card list">
          {companies.data.map((c) => (
            <div key={c.id} className="list-row" role="button" tabIndex={0} onClick={() => setEditing(c)} onKeyDown={(e) => e.key === 'Enter' && setEditing(c)} style={{ cursor: 'pointer' }}>
              <span className="list-icon" style={{ background: 'var(--ws-teal)' }}><Building2 size={16} /></span>
              <span className="grow">
                <div>{c.name} {c.sector && <span className="pill" style={{ height: 20, fontSize: 11, padding: '0 8px', marginLeft: 4 }}>{CRM_SECTOR_LABELS[c.sector]}</span>}</div>
                <div className="secondary">{[c.industry, c.domain].filter(Boolean).join(' · ') || '—'} · {c.contactCount} contatti · {c.openDealCount} trattative aperte</div>
              </span>
            </div>
          ))}
        </div>
      )}
      <CompanySheet
        key={editing === 'new' ? `new-${newSeq.current}` : (editing?.id ?? 'closed')}
        workspace={workspace} company={editing === 'new' ? null : editing} isNew={editing === 'new'} open={editing !== null}
        onClose={() => setEditing(null)} onDelete={editing !== 'new' && editing ? () => del.mutate(editing.id, { onSuccess: () => setEditing(null) }) : undefined}
      />
    </>
  );
}

function CompanySheet({ workspace, company, isNew, open, onClose, onDelete }: { workspace: Workspace; company: CrmCompanySummary | null; isNew: boolean; open: boolean; onClose: () => void; onDelete?: () => void }) {
  const { orgId } = useOrgCtx();
  const mutate = useCrmCompanyMutation(orgId, workspace.id);
  // The list only has the summary; the full "scheda cliente" (sector report fields) needs the detail fetch.
  const detail = useCrmCompany(orgId, workspace.id, isNew ? null : (company?.id ?? null));
  const toast = useToast();
  const blank = { name: '', domain: '', category: '', sector: null as CrmSector | null, notes: '', linkedinUrl: '', sheetData: {} as Record<string, unknown> };
  const [form, setForm] = useState(blank);
  const [key, setKey] = useState(isNew ? 'new' : 'loading');
  const wantKey = isNew ? 'new' : (detail.data ? detail.data.id : 'loading');
  if (wantKey !== key) {
    setForm(isNew || !detail.data ? blank : {
      name: detail.data.name, domain: detail.data.domain ?? '', category: detail.data.industry ?? '', sector: detail.data.sector,
      notes: detail.data.notes ?? '', linkedinUrl: detail.data.linkedinUrl ?? '', sheetData: detail.data.sheetData ?? {},
    });
    setKey(wantKey);
  }
  const sheetFields = form.sector ? CRM_SECTOR_FIELDS[form.sector] : [];
  const setField = (k: string, v: unknown) => setForm((f) => ({ ...f, sheetData: { ...f.sheetData, [k]: v } }));

  if (!isNew && !detail.data) return <Sheet open={open} onClose={onClose} variant="center" title="Azienda" labelledBy="company-title"><PageLoader /></Sheet>;

  return (
    <Sheet open={open} onClose={onClose} variant="center" full title={isNew ? 'Nuova azienda' : 'Scheda cliente'} labelledBy="company-title"
      footer={
        <div style={{ display: 'flex', gap: 8, width: '100%' }}>
          {onDelete && <button type="button" className="btn btn-plain" onClick={() => { if (confirm('Eliminare questa azienda?')) onDelete(); }}><Trash2 size={16} /></button>}
          <span className="spacer" />
          <button type="button" className="btn btn-secondary" onClick={onClose}>Annulla</button>
          <button type="button" className="btn btn-primary" disabled={!form.name.trim() || mutate.isPending}
            onClick={() => mutate.mutate(
              { id: company?.id, patch: { name: form.name, domain: form.domain || null, industry: form.category || null, notes: form.notes || null, linkedinUrl: form.linkedinUrl || null, sector: form.sector, sheetData: form.sheetData } },
              { onSuccess: onClose, onError: (e) => toast(e instanceof ApiError ? e.message : 'Salvataggio non riuscito') },
            )}>Salva</button>
        </div>
      }>
      <label className="field"><span>Nome</span><input className="input" value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })} /></label>
      <div style={{ display: 'flex', gap: 10 }}>
        <label className="field" style={{ flex: 1 }}><span>Sito web</span><input className="input" value={form.domain} onChange={(e) => setForm({ ...form, domain: e.target.value })} placeholder="acme.it" /></label>
        <label className="field" style={{ flex: 1 }}><span>Categoria (facoltativa)</span><input className="input" value={form.category} onChange={(e) => setForm({ ...form, category: e.target.value })} placeholder="es. Abbigliamento" /></label>
      </div>
      <label className="field"><span>LinkedIn</span><input className="input" type="url" value={form.linkedinUrl} onChange={(e) => setForm({ ...form, linkedinUrl: e.target.value })} placeholder="https://www.linkedin.com/company/…" /></label>

      <hr className="sep" />
      <div className="field">
        <span>Settore della scheda cliente</span>
        <select className="input" value={form.sector ?? ''} onChange={(e) => setForm({ ...form, sector: (e.target.value || null) as CrmSector | null })}>
          <option value="">— Scegli per attivare i campi di report —</option>
          {CRM_SECTORS.map((s) => <option key={s} value={s}>{CRM_SECTOR_LABELS[s]}</option>)}
        </select>
      </div>
      {form.sector && (
        <p className="hint" style={{ marginTop: -6 }}>
          Questi dati alimentano i grafici del Report: usali per tenere traccia di budget, KPI e informazioni utili all’analisi interna.
        </p>
      )}
      {sheetFields.map((f) => (
        <label key={f.key} className="field">
          <span>{f.label}{f.suffix ? ` (${f.suffix})` : ''}</span>
          {f.type === 'textarea' ? (
            <textarea className="textarea" rows={3} value={(form.sheetData[f.key] as string) ?? ''} onChange={(e) => setField(f.key, e.target.value)} />
          ) : (
            <input className="input" type={f.type} value={(form.sheetData[f.key] as string | number) ?? ''} onChange={(e) => setField(f.key, f.type === 'number' ? Number(e.target.value) : e.target.value)} />
          )}
        </label>
      ))}
      {sheetFields.length > 0 && <hr className="sep" />}
      <label className="field"><span>Note libere</span><textarea className="textarea" rows={3} value={form.notes} onChange={(e) => setForm({ ...form, notes: e.target.value })} /></label>
    </Sheet>
  );
}

// ------------------------------------------------------------------ contacts
function Contacts({ workspace, canCreate }: { workspace: Workspace; canCreate: boolean }) {
  const { orgId } = useOrgCtx();
  const contacts = useCrmContacts(orgId, workspace.id);
  const del = useDeleteCrmContact(orgId, workspace.id);
  const [editing, setEditing] = useState<CrmContactSummary | 'new' | null>(null);
  const newSeq = useRef(0);
  const openNew = () => { newSeq.current += 1; setEditing('new'); };

  return (
    <>
      <div className="toolbar">{canCreate && <button type="button" className="btn btn-primary btn-sm" onClick={openNew}><Plus size={16} /> Contatto</button>}</div>
      {contacts.isLoading && <PageLoader />}
      {contacts.data?.length === 0 && <div className="card"><EmptyState tone="neutral" title="Nessun contatto">Aggiungi le persone con cui siete in relazione.</EmptyState></div>}
      {contacts.data && contacts.data.length > 0 && (
        <div className="card list">
          {contacts.data.map((c) => (
            <div key={c.id} className="list-row" role="button" tabIndex={0} onClick={() => setEditing(c)} onKeyDown={(e) => e.key === 'Enter' && setEditing(c)} style={{ cursor: 'pointer' }}>
              <span className="list-icon" style={{ background: 'var(--ws-pink)' }}><UsersIcon size={16} /></span>
              <span className="grow">
                <div>{c.firstName} {c.lastName}</div>
                <div className="secondary">{[c.roleTitle, c.companyName].filter(Boolean).join(' · ') || c.email || c.phone || '—'}</div>
              </span>
              {c.phone && <Phone size={15} color="var(--label-3)" />}
            </div>
          ))}
        </div>
      )}
      <ContactSheet
        key={editing === 'new' ? `new-${newSeq.current}` : (editing?.id ?? 'closed')}
        workspace={workspace} contact={editing === 'new' ? null : editing} isNew={editing === 'new'} open={editing !== null}
        onClose={() => setEditing(null)} onDelete={editing !== 'new' && editing ? () => del.mutate(editing.id, { onSuccess: () => setEditing(null) }) : undefined}
      />
    </>
  );
}

function ContactSheet({ workspace, contact, isNew, open, onClose, onDelete }: { workspace: Workspace; contact: CrmContactSummary | null; isNew: boolean; open: boolean; onClose: () => void; onDelete?: () => void }) {
  const { orgId } = useOrgCtx();
  const companies = useCrmCompanies(orgId, workspace.id);
  const mutate = useCrmContactMutation(orgId, workspace.id);
  // The list only has the summary; notes and the LinkedIn URl need the detail fetch.
  const detail = useCrmContact(orgId, workspace.id, isNew ? null : (contact?.id ?? null));
  const toast = useToast();
  const blank = { firstName: '', lastName: '', email: '', phone: '', roleTitle: '', companyId: '', notes: '', linkedinUrl: '' };
  const [form, setForm] = useState(blank);
  const [key, setKey] = useState(isNew ? 'new' : 'loading');
  const wantKey = isNew ? 'new' : (detail.data ? detail.data.id : 'loading');
  if (wantKey !== key) {
    setForm(isNew || !detail.data ? blank : {
      firstName: detail.data.firstName, lastName: detail.data.lastName, email: detail.data.email ?? '', phone: detail.data.phone ?? '',
      roleTitle: detail.data.roleTitle ?? '', companyId: detail.data.companyId ?? '', notes: detail.data.notes ?? '', linkedinUrl: detail.data.linkedinUrl ?? '',
    });
    setKey(wantKey);
  }

  if (!isNew && !detail.data) return <Sheet open={open} onClose={onClose} variant="center" title="Contatto" labelledBy="contact-title"><PageLoader /></Sheet>;

  return (
    <Sheet open={open} onClose={onClose} variant="center" title={isNew ? 'Nuovo contatto' : 'Modifica contatto'} labelledBy="contact-title"
      footer={
        <div style={{ display: 'flex', gap: 8, width: '100%' }}>
          {onDelete && <button type="button" className="btn btn-plain" onClick={() => { if (confirm('Eliminare questo contatto?')) onDelete(); }}><Trash2 size={16} /></button>}
          <span className="spacer" />
          <button type="button" className="btn btn-secondary" onClick={onClose}>Annulla</button>
          <button type="button" className="btn btn-primary" disabled={!form.firstName.trim() || mutate.isPending}
            onClick={() => mutate.mutate(
              { id: contact?.id, patch: { firstName: form.firstName, lastName: form.lastName, email: form.email || null, phone: form.phone || null, roleTitle: form.roleTitle || null, companyId: form.companyId || null, notes: form.notes || null, linkedinUrl: form.linkedinUrl || null } },
              { onSuccess: onClose, onError: (e) => toast(e instanceof ApiError ? e.message : 'Salvataggio non riuscito') },
            )}>Salva</button>
        </div>
      }>
      <div style={{ display: 'flex', gap: 10 }}>
        <label className="field" style={{ flex: 1 }}><span>Nome</span><input className="input" value={form.firstName} onChange={(e) => setForm({ ...form, firstName: e.target.value })} /></label>
        <label className="field" style={{ flex: 1 }}><span>Cognome</span><input className="input" value={form.lastName} onChange={(e) => setForm({ ...form, lastName: e.target.value })} /></label>
      </div>
      <label className="field"><span>Email</span><input className="input" type="email" value={form.email} onChange={(e) => setForm({ ...form, email: e.target.value })} /></label>
      <label className="field"><span>Telefono</span><input className="input" value={form.phone} onChange={(e) => setForm({ ...form, phone: e.target.value })} /></label>
      <label className="field"><span>Ruolo</span><input className="input" value={form.roleTitle} onChange={(e) => setForm({ ...form, roleTitle: e.target.value })} placeholder="es. Responsabile marketing" /></label>
      <div className="field">
        <span>Azienda</span>
        <select className="input" value={form.companyId} onChange={(e) => setForm({ ...form, companyId: e.target.value })}>
          <option value="">—</option>
          {companies.data?.map((c) => <option key={c.id} value={c.id}>{c.name}</option>)}
        </select>
      </div>
      <label className="field"><span>LinkedIn</span><input className="input" type="url" value={form.linkedinUrl} onChange={(e) => setForm({ ...form, linkedinUrl: e.target.value })} placeholder="https://www.linkedin.com/in/…" /></label>
      <label className="field"><span>Note</span><textarea className="textarea" rows={3} value={form.notes} onChange={(e) => setForm({ ...form, notes: e.target.value })} /></label>
      {!isNew && detail.data && (
        <>
          <hr className="sep" />
          <h3 className="subhead">Email</h3>
          <EmailComposer workspace={workspace} contactId={detail.data.id} contactEmail={detail.data.email} />
        </>
      )}
    </Sheet>
  );
}
